// app/javascript/controllers/hotspot_editor_controller.js
import { Controller } from "@hotwired/stimulus"

// managers
import {
  initViewer,
  loadTextureFromUrl,
  computeLimitsDeg,
  setCenteredView,
  lonLatFromScreen,
  renderHotspots,
  updateHotspotPositions,
  startLoop,
  stopLoop,
  bindResize,
  unbindResize,
  disposeAll,
} from "../scenes_editor/hotspots_manager"

import {
  initTools,
  renderToolsSection,
} from "../scenes_editor/tools_manager"

import {
  initInteractions,
  ensureTmpInteractionsBucket,
  moveInteractionsBucket,
  deleteInteractionsBucket,
  renderInteractionsList,
  updateInteractionsUIState,
  loadInteractionsFromServer,
  addTextInteraction,
  addQuizInteraction,
  buildInteractionsAttributes,
} from "../scenes_editor/interactions_manager"

import {
  initWorkflowEditor,
  destroyWorkflowEditor,
  buildWorkflowPayload,
  saveWorkflow,
} from "../scenes_editor/workflow_manager"

const DEFAULT_LABEL = ""
const DEFAULT_ICON = ""
const DEFAULT_CSS = "hs-dot hs-dot--lg"
const DEFAULT_ACTIVE = true

export default class extends Controller {
  static targets = [
    "layer",
    "btnLeft",
    "btnRight",
    "btnCenter",
    "sidebar",

    // generale
    "inputName",
    "inputLabel",
    "inputIcon",
    "inputCss",
    "inputActive",

    // step / pannelli
    "sectionGeneral",
    "sectionInteractions",
    "sectionTools",
    "panelGeneral",
    "panelInteractions",
    "panelTools",
    "chevGeneral",
    "chevInteractions",
    "chevTools",

    // tools
    "toolsList",

    // interazioni
    "interactionsList",
    "btnAddText",
    "btnAddQuiz",
    "interactionsHint",

    // insert mode + chips
    "btnInsert",
    "insertOverlay",
    "chipTotal",
    "chipActive",
    "chipInsert",
  ]

  static values = {
    projection: String,
    backgroundUrl: String,
    hotspots: Array,

    availableTools: Array,
    workflowSteps: Array,
    workflowUpdateUrl: String,

    createUrl: String,
    updateUrlTemplate: String,
    destroyUrlTemplate: String,
    csrfToken: String,

    interactionsIndexUrlTemplate: String,
  }

  connect() {
    this.viewerEl = this.element.querySelector("#pano-editor-viewer")
    if (!this.viewerEl) return

    if (!this.backgroundUrlValue) {
      console.warn("[EDITOR] backgroundUrlValue vuoto")
      return
    }

    this.initializeState()
    this.initializeManagers()
    this.initializeViewer()
    this.initializeUI()
    this.initializeEvents()

    this.hideSidebar()
    this.refreshChips()
    this.renderPanels()
    renderHotspots(this)
  }

  disconnect() {
    this.unbindEvents()

    stopLoop(this)
    unbindResize(this)
    this.unbindButtons?.()

    destroyWorkflowEditor(this)
    disposeAll(this)
  }

  initializeState() {
    this.openPanel = "general"

    this.hotspots = Array.isArray(this.hotspotsValue)
      ? [...this.hotspotsValue].filter((h) => h.is_active !== false)
      : []

    this.workflowSteps = Array.isArray(this.workflowStepsValue)
      ? [...this.workflowStepsValue]
      : []

    this.hotspotEls = new Map()
    this.originalById = new Map()

    this.selectedId = null
    this.editingId = null

    this.dragging = false
    this._dragPointerId = null
    this._dragFromToolbar = false

    this.insertMode = false

    this._onBgClick = null
    this._onKeyDown = null
    this._onPointerMove = null
    this._onPointerUp = null

    console.log("workflowSteps", this.workflowSteps)
  }

  initializeManagers() {
    initTools(this)
    initInteractions(this)
    initWorkflowEditor(this)
  }

  initializeViewer() {
    initViewer(this)
    loadTextureFromUrl(this, this.backgroundUrlValue)

    computeLimitsDeg(this)
    setCenteredView(this)

    startLoop(this)
    bindResize(this)
  }

  initializeUI() {
    this.bindButtons()

    if (this.hasBtnAddTextTarget) {
      this._onAddTextClick = (e) => addTextInteraction(this, e)
      this.btnAddTextTarget.addEventListener("click", this._onAddTextClick)
    }

    if (this.hasBtnAddQuizTarget) {
      this._onAddQuizClick = (e) => addQuizInteraction(this, e)
      this.btnAddQuizTarget.addEventListener("click", this._onAddQuizClick)
    }
  }

  initializeEvents() {
    this._onBgClick = (e) => {
      if (this.dragging) return

      if (this.insertMode) {
        this.createHotspotAtPointer(e)
        return
      }

      this.selectHotspot(null)
    }

    this._onKeyDown = (e) => {
      if (e.key === "Escape" && this.insertMode) {
        this.setInsertMode(false)
      }
    }

    this._onPointerMove = this.onPointerMove.bind(this)
    this._onPointerUp = this.onPointerUp.bind(this)

    this.viewerEl.addEventListener("click", this._onBgClick)
    window.addEventListener("keydown", this._onKeyDown)
    window.addEventListener("pointermove", this._onPointerMove)
    window.addEventListener("pointerup", this._onPointerUp)
    window.addEventListener("pointercancel", this._onPointerUp)
  }

  unbindEvents() {
    this.viewerEl?.removeEventListener("click", this._onBgClick)
    window.removeEventListener("keydown", this._onKeyDown)
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
    window.removeEventListener("pointercancel", this._onPointerUp)

    if (this.hasBtnAddTextTarget && this._onAddTextClick) {
      this.btnAddTextTarget.removeEventListener("click", this._onAddTextClick)
    }

    if (this.hasBtnAddQuizTarget && this._onAddQuizClick) {
      this.btnAddQuizTarget.removeEventListener("click", this._onAddQuizClick)
    }

    this._onBgClick = null
    this._onKeyDown = null
    this._onPointerMove = null
    this._onPointerUp = null
    this._onAddTextClick = null
    this._onAddQuizClick = null
  }

  /* =========================
   * CHIPS
   * ========================= */
  refreshChips() {
    const total = Array.isArray(this.hotspots) ? this.hotspots.length : 0
    const active = Array.isArray(this.hotspots)
      ? this.hotspots.filter((h) => h.is_active !== false).length
      : 0

    if (this.hasChipTotalTarget) this.chipTotalTarget.textContent = String(total)
    if (this.hasChipActiveTarget) this.chipActiveTarget.textContent = String(active)
  }

  /* =========================
   * INSERT MODE
   * ========================= */
  toggleInsertMode(e) {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    this.setInsertMode(!this.insertMode)
  }

  setInsertMode(on) {
    this.insertMode = !!on

    if (this.hasInsertOverlayTarget) {
      this.insertOverlayTarget.classList.toggle("hidden", !this.insertMode)
    }

    if (this.hasChipInsertTarget) {
      this.chipInsertTarget.classList.toggle("hidden", !this.insertMode)
    }

    if (this.hasBtnInsertTarget) {
      if (this.insertMode) {
        this.btnInsertTarget.classList.remove("bg-blue-600", "hover:bg-blue-700")
        this.btnInsertTarget.classList.add("bg-gray-800", "hover:bg-gray-900")
        this.btnInsertTarget.textContent = "✕ Annulla inserimento"
      } else {
        this.btnInsertTarget.classList.remove("bg-gray-800", "hover:bg-gray-900")
        this.btnInsertTarget.classList.add("bg-blue-600", "hover:bg-blue-700")
        this.btnInsertTarget.textContent = "➕ Inserisci nuovo hotspot"
      }
    }

    if (this.viewerEl) {
      this.viewerEl.style.cursor = this.insertMode ? "crosshair" : ""
    }
  }

  addHotspot(e) {
    this.toggleInsertMode(e)
  }

  createHotspotAtPointer(e) {
    const pos = lonLatFromScreen(this, e.clientX, e.clientY)
    const lon = pos?.lon ?? 0
    const lat = pos?.lat ?? 0

    this.addHotspotAt(lon, lat)
    this.setInsertMode(false)
  }

  addHotspotAt(lon, lat) {
    const tmpId = `tmp_${Date.now()}`

    const hs = {
      id: tmpId,
      name: "",
      label: DEFAULT_LABEL,
      hotspot_type: "info",
      icon: DEFAULT_ICON,
      css_classes: DEFAULT_CSS,
      lon,
      lat,
      is_active: DEFAULT_ACTIVE,
      _isNew: true,
    }

    this.hotspots.push(hs)

    ensureTmpInteractionsBucket(this, tmpId)

    renderHotspots(this)
    this.refreshChips()

    this.enableEditing(tmpId)
    this.selectHotspot(tmpId)

    this.dragging = false
    this._dragPointerId = null
    this._dragFromToolbar = false
  }

  /* =========================
   * ACCORDION
   * ========================= */
  togglePanel(e) {
    const panel = e?.params?.panel
    if (!panel) return

    this.openPanel = this.openPanel === panel ? null : panel
    this.renderPanels()
  }

  renderPanels() {
    const isGeneral = this.openPanel === "general"
    const isInteractions = this.openPanel === "interactions"
    const isTools = this.openPanel === "tools"

    if (this.hasPanelGeneralTarget) this.panelGeneralTarget.hidden = !isGeneral
    if (this.hasPanelInteractionsTarget) this.panelInteractionsTarget.hidden = !isInteractions
    if (this.hasPanelToolsTarget) this.panelToolsTarget.hidden = !isTools

    if (this.hasSectionGeneralTarget) this.sectionGeneralTarget.classList.toggle("is-open", isGeneral)
    if (this.hasSectionInteractionsTarget) this.sectionInteractionsTarget.classList.toggle("is-open", isInteractions)
    if (this.hasSectionToolsTarget) this.sectionToolsTarget.classList.toggle("is-open", isTools)

    if (this.hasChevGeneralTarget) this.chevGeneralTarget.textContent = isGeneral ? "▴" : "▾"
    if (this.hasChevInteractionsTarget) this.chevInteractionsTarget.textContent = isInteractions ? "▴" : "▾"
    if (this.hasChevToolsTarget) this.chevToolsTarget.textContent = isTools ? "▴" : "▾"
  }

  /* =========================
   * SIDEBAR
   * ========================= */
  showSidebarFor(hs) {
    if (!this.hasSidebarTarget || !hs) return

    this.openPanel = "general"
    this.renderPanels()

    if (this.hasInputNameTarget) this.inputNameTarget.value = hs.name || ""
    if (this.hasInputLabelTarget) this.inputLabelTarget.value = DEFAULT_LABEL
    if (this.hasInputIconTarget) this.inputIconTarget.value = DEFAULT_ICON
    if (this.hasInputCssTarget) this.inputCssTarget.value = DEFAULT_CSS
    if (this.hasInputActiveTarget) this.inputActiveTarget.checked = DEFAULT_ACTIVE

    renderToolsSection(this, hs)
    renderInteractionsList(this, hs.id)
    updateInteractionsUIState(this, hs)

    this.sidebarTarget.hidden = false

    if (!String(hs.id).startsWith("tmp_")) {
      loadInteractionsFromServer(this, hs.id)
    }
  }

  hideSidebar() {
    if (!this.hasSidebarTarget) return
    this.openPanel = "general"
    this.sidebarTarget.hidden = true
  }

  /* =========================
   * HOTSPOT SELECTION
   * ========================= */
  renderHotspots() {
    renderHotspots(this)
  }

  selectHotspot(id) {
    this.selectedId = id

    if (id === null) {
      this.editingId = null
      this.hideSidebar()
    }

    for (const [hsId, el] of this.hotspotEls.entries()) {
      const isSelected = id !== null && hsId === id
      const isEditing = this.editingId === hsId

      el.classList.toggle("is-active", isSelected)
      el.classList.toggle("is-editing", isEditing)

      const existingTools = el.querySelector(".hs-dot__tools")
      if (existingTools) existingTools.remove()

      if (isSelected) {
        el.appendChild(this.createToolsBar(hsId))
      }
    }

    if (id !== null) {
      const hs = this.hotspots.find((h) => h.id === id)
      if (hs) {
        renderToolsSection(this, hs)
        renderInteractionsList(this, hs.id)
      }
    }
  }

  createToolsBar(hsId) {
    const tools = document.createElement("div")
    tools.className = "hs-dot__tools"

    const isEditing = this.editingId === hsId

    const btnEditSave = document.createElement("div")
    btnEditSave.className = "hs-dot__btn"
    btnEditSave.textContent = isEditing ? "💾" : "✎"

    btnEditSave.addEventListener("pointerdown", (e) => {
      e.preventDefault()
      e.stopPropagation()
    })

    btnEditSave.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()

      if (this.editingId === hsId) {
        this.saveHotspot(hsId)
      } else {
        this.enableEditing(hsId)
      }
    })

    tools.appendChild(btnEditSave)

    if (!isEditing) {
      const btnDelete = document.createElement("div")
      btnDelete.className = "hs-dot__btn hs-dot__btn--danger"
      btnDelete.textContent = "✕"

      btnDelete.addEventListener("pointerdown", (e) => {
        e.preventDefault()
        e.stopPropagation()
      })

      btnDelete.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.deleteHotspot(hsId)
      })

      tools.appendChild(btnDelete)
    } else {
      const btnCancel = document.createElement("div")
      btnCancel.className = "hs-dot__btn"
      btnCancel.textContent = "↩"

      btnCancel.addEventListener("pointerdown", (e) => {
        e.preventDefault()
        e.stopPropagation()
      })

      btnCancel.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.cancelEditing(hsId)
      })

      tools.appendChild(btnCancel)
    }

    return tools
  }

  enableEditing(hsId) {
    const hs = this.hotspots.find((h) => h.id === hsId)
    if (!hs) return

    this.originalById.set(hsId, {
      lon: hs.lon,
      lat: hs.lat,
      name: hs.name,
      label: hs.label,
      hotspot_type: hs.hotspot_type,
      icon: hs.icon,
      css_classes: hs.css_classes,
      is_active: hs.is_active,
    })

    this.editingId = hsId
    this.showSidebarFor(hs)
    this.selectHotspot(hsId)
  }

  cancelEditing(hsId) {
    const hs = this.hotspots.find((h) => h.id === hsId)
    const orig = this.originalById.get(hsId)

    if (hs && orig) {
      hs.lon = orig.lon
      hs.lat = orig.lat
      hs.name = orig.name
      hs.label = orig.label
      hs.hotspot_type = orig.hotspot_type
      hs.icon = orig.icon
      hs.css_classes = orig.css_classes
      hs.is_active = orig.is_active

      updateHotspotPositions(this)
    }

    this.hideSidebar()
    this.editingId = null
    this.selectHotspot(hsId)
  }

  /* =========================
   * SAVE / CREATE / DELETE
   * ========================= */
  async saveHotspot(hsId) {
    const hs = this.hotspots.find((h) => h.id === hsId)
    if (!hs) return

    hs.name = this.hasInputNameTarget ? this.inputNameTarget.value : ""
    hs.hotspot_type = "info"

    hs.label = DEFAULT_LABEL
    hs.icon = DEFAULT_ICON
    hs.css_classes = DEFAULT_CSS
    hs.is_active = DEFAULT_ACTIVE

    const selectedSet = this.selectedToolIdsByHotspotId?.get(hs.id) || new Set()
    hs._tool_ids = Array.from(selectedSet)

    try {
      if (String(hsId).startsWith("tmp_")) {
        await this.createHotspotOnServer(hsId)
      } else {
        await this.updateHotspotOnServer(hsId)
      }
    } catch (err) {
      console.error("[EDITOR] save error", err)
      return
    }

    this.hideSidebar()
    this.editingId = null
    this.selectHotspot(hs.id)
    this.refreshChips()
  }

  async updateHotspotOnServer(hsId) {
    const hs = this.hotspots.find((h) => h.id === hsId)
    if (!hs) return

    const url = this.updateUrlTemplateValue.replace(":id", hsId)

    const resp = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
        Accept: "application/json",
      },
      body: JSON.stringify({
        hotspot: {
          lon: hs.lon,
          lat: hs.lat,
          name: hs.name,
          label: hs.label,
          hotspot_type: hs.hotspot_type,
          icon: hs.icon,
          css_classes: hs.css_classes,
          is_active: hs.is_active,
          pulse_tool_ids: hs._tool_ids || [],
          interactions_attributes: buildInteractionsAttributes(this, hs.id),
        },
      }),
    })

    if (!resp.ok) {
      const txt = await resp.text()
      console.error("[EDITOR] update failed", resp.status, txt)
      throw new Error("update failed")
    }
  }

  async createHotspotOnServer(tmpId) {
    const hs = this.hotspots.find((h) => h.id === tmpId)
    if (!hs) return

    const resp = await fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
        Accept: "application/json",
      },
      body: JSON.stringify({
        hotspot: {
          lon: hs.lon,
          lat: hs.lat,
          name: hs.name,
          label: hs.label,
          hotspot_type: hs.hotspot_type,
          icon: hs.icon,
          css_classes: hs.css_classes,
          is_active: hs.is_active,
          pulse_tool_ids: hs._tool_ids || [],
          interactions_attributes: buildInteractionsAttributes(this, tmpId),
        },
      }),
    })

    if (!resp.ok) {
      const txt = await resp.text()
      console.error("[EDITOR] create failed", resp.status, txt)
      throw new Error("create failed")
    }

    const data = await resp.json().catch(() => null)
    const newId = data?.id
    if (!newId) return

    if (this.selectedToolIdsByHotspotId?.has(tmpId)) {
      const set = this.selectedToolIdsByHotspotId.get(tmpId)
      this.selectedToolIdsByHotspotId.delete(tmpId)
      this.selectedToolIdsByHotspotId.set(newId, set)
    }

    moveInteractionsBucket(this, tmpId, newId)

    hs.id = newId

    if (this.originalById.has(tmpId)) {
      const orig = this.originalById.get(tmpId)
      this.originalById.delete(tmpId)
      this.originalById.set(newId, orig)
    }

    if (this.selectedId === tmpId) this.selectedId = newId
    if (this.editingId === tmpId) this.editingId = newId

    renderHotspots(this)

    const hsNow = this.hotspots.find((h) => h.id === newId)
    if (hsNow) {
      updateInteractionsUIState(this, hsNow)
      renderInteractionsList(this, newId)
    }

    this.refreshChips()
  }

  async deleteHotspot(hsId) {
    if (String(hsId).startsWith("tmp_")) {
      this.hotspots = this.hotspots.filter((h) => h.id !== hsId)
      this.selectedToolIdsByHotspotId?.delete(hsId)
      deleteInteractionsBucket(this, hsId)

      if (this.editingId === hsId) {
        this.editingId = null
        this.hideSidebar()
      }

      this.selectHotspot(null)
      renderHotspots(this)
      this.refreshChips()
      return
    }

    if (!confirm("Sei sicuro di voler eliminare questo hotspot?")) return

    const url = this.destroyUrlTemplateValue.replace(":id", hsId)

    const resp = await fetch(url, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": this.csrfTokenValue,
        Accept: "application/json",
      },
    })

    if (!resp.ok) {
      const txt = await resp.text()
      console.error("[EDITOR] delete failed", resp.status, txt)
      return
    }

    this.hotspots = this.hotspots.filter((h) => h.id !== hsId)
    this.selectedToolIdsByHotspotId?.delete(hsId)
    deleteInteractionsBucket(this, hsId)

    if (this.editingId === hsId) {
      this.editingId = null
      this.hideSidebar()
    }

    this.selectHotspot(null)
    renderHotspots(this)
    this.refreshChips()
  }

  /* =========================
   * DRAG HOTSPOT
   * ========================= */
  onPointerMove(e) {
    if (!this.dragging || !this.editingId) return

    const pos = lonLatFromScreen(this, e.clientX, e.clientY)
    if (!pos) return

    const hs = this.hotspots.find((h) => h.id === this.editingId)
    if (!hs) return

    hs.lon = pos.lon
    hs.lat = pos.lat

    updateHotspotPositions(this)
  }

  onPointerUp() {
    if (!this.dragging) return

    this.dragging = false

    const el = this.hotspotEls.get(this.editingId)
    if (el && this._dragPointerId != null) {
      try {
        el.releasePointerCapture?.(this._dragPointerId)
      } catch (_) {}
    }

    this._dragPointerId = null
    this._dragFromToolbar = false
  }

  /* =========================
   * BUTTONS VIEWER
   * ========================= */
  bindButtons() {
    this._unbindHoldLeft = this.bindHoldButton(
      this.hasBtnLeftTarget ? this.btnLeftTarget : null,
      "left"
    )

    this._unbindHoldRight = this.bindHoldButton(
      this.hasBtnRightTarget ? this.btnRightTarget : null,
      "right"
    )

    this._onCenter = (e) => {
      e.preventDefault()
      setCenteredView(this)
    }

    if (this.hasBtnCenterTarget) {
      this.btnCenterTarget.addEventListener("pointerdown", this._onCenter)
    }
  }

  unbindButtons() {
    this._unbindHoldLeft?.()
    this._unbindHoldRight?.()

    if (this._onCenter && this.hasBtnCenterTarget) {
      this.btnCenterTarget.removeEventListener("pointerdown", this._onCenter)
    }

    this._onCenter = null
  }

  bindHoldButton(btn, dir) {
    if (!btn) return () => {}

    const set = (value) => {
      if (dir === "left") this.uiLeft = value
      if (dir === "right") this.uiRight = value
      this.updateButtons()
    }

    const down = (e) => {
      e.preventDefault()
      btn.setPointerCapture?.(e.pointerId)
      set(true)
    }

    const release = (e) => {
      try {
        btn.releasePointerCapture?.(e.pointerId)
      } catch (_) {}
      set(false)
    }

    const leave = () => set(false)

    btn.addEventListener("pointerdown", down)
    btn.addEventListener("pointerup", release)
    btn.addEventListener("pointercancel", release)
    btn.addEventListener("pointerleave", leave)

    return () => {
      btn.removeEventListener("pointerdown", down)
      btn.removeEventListener("pointerup", release)
      btn.removeEventListener("pointercancel", release)
      btn.removeEventListener("pointerleave", leave)
    }
  }

  updateButtons() {
    if (this.hasBtnLeftTarget) {
      this.btnLeftTarget.classList.toggle("active", !!this.uiLeft)
    }

    if (this.hasBtnRightTarget) {
      this.btnRightTarget.classList.toggle("active", !!this.uiRight)
    }

    const isCentered = Math.abs(this.lon) < 0.1 && Math.abs(this.lat) < 0.1

    if (this.hasBtnCenterTarget) {
      this.btnCenterTarget.classList.toggle("active", !isCentered)
    }
  }

  /* =========================
   * WORKFLOW
   * ========================= */
  workflowToggleUse() {
    // il comportamento vero è gestito nel workflow_manager
  }

  workflowFieldChanged() {
    // sync gestita nel workflow_manager
  }

  async saveWorkflow() {
    const result = await saveWorkflow(this)

    if (result.ok) {
      alert("Workflow salvato correttamente.")
      return
    }

    alert(result.error || "Errore durante il salvataggio del workflow.")
  }

  debugWorkflowPayload() {
    const payload = buildWorkflowPayload(this)
    console.log("workflow payload", payload)
  }
}